import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Image as ImageIcon, RefreshCw, Loader2, Check } from 'lucide-react';
import { loadScanner, setPendingScan, type Scanner } from '../lib/scanner';
import { useToast } from '../components/Toast';

/**
 * Scansione guidata (§6.16). Camera a tutto schermo con edge-detection
 * (jscanify/OpenCV) e auto-crop; l'immagine ritagliata viene passata al Form
 * (§6.6). Nessun OCR. Fallback a "Galleria" se la camera non è disponibile.
 */
export function ScansioneScreen() {
  const navigate = useNavigate();
  const { show } = useToast();

  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scannerRef = useRef<Scanner | null>(null);
  const rafRef = useRef<number>(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const [side, setSide] = useState<'front' | 'back'>('front');
  const [detected, setDetected] = useState(false);
  const [status, setStatus] = useState<'init' | 'ready' | 'nocam'>('init');
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (!alive) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setStatus('ready');
        // Edge-detection in background (non blocca l'uso della camera).
        loadScanner()
          .then((s) => {
            scannerRef.current = s;
            startDetectionLoop();
          })
          .catch(() => {
            /* senza auto-crop si scatta comunque la foto intera */
          });
      } catch {
        if (alive) setStatus('nocam');
      }
    })();
    return () => {
      alive = false;
      cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startDetectionLoop = () => {
    const tmp = document.createElement('canvas');
    const tick = () => {
      const video = videoRef.current;
      const overlay = overlayRef.current;
      const scanner = scannerRef.current;
      if (video && overlay && scanner && video.videoWidth) {
        tmp.width = video.videoWidth;
        tmp.height = video.videoHeight;
        const tctx = tmp.getContext('2d');
        if (tctx) {
          tctx.drawImage(video, 0, 0);
          try {
            const hl = scanner.highlightPaper(tmp, { color: '#E8B24A', thickness: 6 });
            overlay.width = hl.width;
            overlay.height = hl.height;
            const octx = overlay.getContext('2d');
            octx?.clearRect(0, 0, overlay.width, overlay.height);
            octx?.drawImage(hl, 0, 0);
            setDetected(true);
          } catch {
            setDetected(false);
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const finishWith = (dataUrl: string) => {
    setPendingScan(dataUrl, `${side === 'front' ? 'fronte' : 'retro'}.jpg`);
    navigate('/nuovo');
  };

  const onShutter = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return show('Camera non pronta');
    setCapturing(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')?.drawImage(video, 0, 0);
      // Auto-crop al documento se lo scanner è pronto, altrimenti frame intero.
      let out: HTMLCanvasElement = canvas;
      if (scannerRef.current) {
        try {
          const w = 1000;
          const h = Math.round((canvas.height / canvas.width) * w);
          out = scannerRef.current.extractPaper(canvas, w, h);
        } catch {
          out = canvas;
        }
      }
      finishWith(out.toDataURL('image/jpeg', 0.92));
    } finally {
      setCapturing(false);
    }
  };

  const onGallery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => finishWith(String(reader.result));
    reader.readAsDataURL(file);
  };

  return (
    <div className="scanner">
      <button className="scanner__close" onClick={() => navigate(-1)} aria-label="Chiudi">
        <X size={22} />
      </button>

      {status === 'nocam' ? (
        <div className="scanner__nocam">
          <p>Camera non disponibile.</p>
          <button className="btn btn--accent" onClick={() => fileRef.current?.click()}>
            <ImageIcon size={18} /> Scegli dalla galleria
          </button>
        </div>
      ) : (
        <>
          <video ref={videoRef} className="scanner__video" playsInline muted />
          <canvas ref={overlayRef} className="scanner__overlay" />
          <div className="scanner__frame" aria-hidden>
            <span className="scanner__corner scanner__corner--tl" />
            <span className="scanner__corner scanner__corner--tr" />
            <span className="scanner__corner scanner__corner--bl" />
            <span className="scanner__corner scanner__corner--br" />
          </div>

          <div className="scanner__chip">
            {status === 'init' ? (
              <><Loader2 size={14} className="spin" /> Avvio camera…</>
            ) : detected ? (
              <><Check size={14} /> Documento rilevato</>
            ) : (
              'Inquadra il documento'
            )}
          </div>
          <div className="scanner__hint">Tieni fermo · inquadra il {side === 'front' ? 'fronte' : 'retro'}</div>
        </>
      )}

      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onGallery} />

      <div className="scanner__bar">
        <button className="scanner__side" onClick={() => fileRef.current?.click()}>
          <ImageIcon size={22} />
          <span>Galleria</span>
        </button>
        <button
          className="scanner__shutter"
          onClick={onShutter}
          disabled={status !== 'ready' || capturing}
          aria-label="Scatta"
        >
          {capturing ? <Loader2 size={26} className="spin" /> : null}
        </button>
        <button className="scanner__side" onClick={() => setSide((s) => (s === 'front' ? 'back' : 'front'))}>
          <RefreshCw size={22} />
          <span>{side === 'front' ? 'Retro' : 'Fronte'}</span>
        </button>
      </div>
    </div>
  );
}
